// Three-step photo upload orchestration (slice 12, ADR 0017):
//
//   1. POST .../photo-upload-url   → presigned PUT URL
//   2. PUT  the file bytes to that URL (XHR, upload progress)
//   3. POST .../photo-confirm      → HEAD-verifies the key and
//                                   bumps menu_items.version
//
// Why XHR for step 2 instead of fetch: fetch's upload stream has no
// progress event anywhere in Safari and only inside a worker in
// Chromium; XHR's `upload.onprogress` works everywhere today. The
// request is a direct-to-storage PUT, so no bearer token and no
// Idempotency-Key — those belong to the API-side bookends.
//
// The orchestrator never retries on its own: a provider "upload
// succeeded, confirm timed out" state must be resolved explicitly by
// the caller (the user clicking Confirm again picks a fresh
// idempotency key per apiFetch, so the server dedupes). See
// CLAUDE.md "Hard rules — provider timeout".

import { ApiError, apiFetch } from './client.js';
import type { PhotoConfirmResponse, PhotoContentType, PhotoUploadUrlResponse } from './types.js';

const ALLOWED_CONTENT_TYPES: readonly PhotoContentType[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
];

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface PhotoUploadArgs {
  readonly basePath: string; // "/organizations/:orgId/menus/:menuId/items/:itemId"
  readonly file: File;
  readonly expectedVersion: number;
  readonly onProgress?: (fraction: number) => void;
  readonly signal?: AbortSignal;
}

export interface PhotoUploadResult {
  readonly objectKey: string;
  readonly confirm: PhotoConfirmResponse;
}

export class PhotoValidationError extends Error {
  public readonly code: 'content_type_not_allowed' | 'file_too_large' | 'file_empty';
  constructor(code: PhotoValidationError['code'], message: string) {
    super(message);
    this.name = 'PhotoValidationError';
    this.code = code;
  }
}

export function assertUploadable(file: File): asserts file is File {
  if (file.size === 0) {
    throw new PhotoValidationError('file_empty', 'file is empty');
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new PhotoValidationError(
      'file_too_large',
      `file is ${(file.size / 1024 / 1024).toFixed(1)} MB (max 10 MB)`,
    );
  }
  if (!ALLOWED_CONTENT_TYPES.includes(file.type as PhotoContentType)) {
    throw new PhotoValidationError(
      'content_type_not_allowed',
      `${file.type || 'unknown'} is not an allowed image type (jpeg, png, webp, avif)`,
    );
  }
}

export async function uploadMenuItemPhoto(args: PhotoUploadArgs): Promise<PhotoUploadResult> {
  assertUploadable(args.file);

  const contentType = args.file.type as PhotoContentType;

  // Step 1 — mint the presigned PUT URL. apiFetch adds bearer +
  // Idempotency-Key.
  const minted = await apiFetch<PhotoUploadUrlResponse>(`${args.basePath}/photo-upload-url`, {
    method: 'POST',
    body: { contentType, sizeBytes: args.file.size },
    ...(args.signal ? { signal: args.signal } : {}),
  });

  // Step 2 — stream the bytes directly to storage. Required headers
  // come from step 1's response (the SDK pinned ContentType into the
  // signature, so we send that exact value or the PUT is rejected).
  await putWithProgress(
    minted.uploadUrl,
    args.file,
    minted.requiredHeaders,
    args.onProgress,
    args.signal,
  );

  // Step 3 — confirm. Server HEADs the key, CAS-updates the item, and
  // writes the outbox + audit rows. A fresh Idempotency-Key is
  // generated by apiFetch per call; if confirm times out and the user
  // clicks again, the server dedupes on idempotency key.
  const confirm = await apiFetch<PhotoConfirmResponse>(`${args.basePath}/photo-confirm`, {
    method: 'POST',
    body: { objectKey: minted.objectKey, expectedVersion: args.expectedVersion },
    ...(args.signal ? { signal: args.signal } : {}),
  });

  return { objectKey: minted.objectKey, confirm };
}

export function putWithProgress(
  url: string,
  file: File,
  requiredHeaders: Readonly<Record<string, string>>,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    for (const [name, value] of Object.entries(requiredHeaders)) {
      xhr.setRequestHeader(name, value);
    }

    if (onProgress) {
      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable && ev.total > 0) {
          onProgress(Math.min(1, ev.loaded / ev.total));
        }
      });
      xhr.upload.addEventListener('loadend', () => onProgress(1));
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(
        new ApiError(
          xhr.status,
          `upload failed with ${xhr.status} ${xhr.statusText}`,
          xhr.responseText,
        ),
      );
    });
    xhr.addEventListener('error', () => {
      reject(new ApiError(0, 'network error during upload', null));
    });
    xhr.addEventListener('abort', () => {
      reject(new ApiError(0, 'upload aborted', null));
    });

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}
